import React from 'react';
import {useSelector, useDispatch} from 'react-redux';
import Text from 'shared/components/text';
import Button from 'src/components/button';
import Icon from 'shared/components/icon';
import TextInput from 'src/components/inputs/text-input';
import Label from 'shared/components/label';
import {Formik, Form, Field} from 'formik';
import OfficialIdUploader from '../containers/official-id-uploader';
import AccountSettingsFormFooter from 'shared/account-settings/components/account-settings-form-footer';
import {updateEducatorInfo} from '../actions/educator';

import {EducatorAccountSettings} from './educator-account-settings';
import {EducatorStore} from 'src/types/store';

interface Props {
  onSubmit: (values: Partial<EducatorAccountSettings>) => void;
  initialValues: EducatorAccountSettings;
}

export const updateField =
  (setFieldValue: (name: string, value: string) => void, name: string) =>
  (e: React.ChangeEvent<HTMLInputElement>) =>
    setFieldValue(name, e.target.value);

const goToLinkedinAuth = () => {
  window.location.href = '/v1/linkedin/connect';
};

const TrustAndVerification = ({onSubmit, initialValues}: Props) => {
  const dispatch = useDispatch();
  const linkedinConnected = useSelector((state: EducatorStore) => state.educator.linkedinConnected);

  const disconnectLinkedin = () => {
    dispatch(
      updateEducatorInfo({
        linkedinConnected: false,
        linkedinProfile: {
          firstName: null,
          lastName: null,
          profileUrl: null,
          profileImage: null,
          profileId: null,
        },
      })
    );
  };

  const {websiteForEmployment} = initialValues;

  const validateWebsiteForEmployment = (values: {websiteForEmployment?: string}) => {
    const errors: Record<string, string> = {};

    if (
      values.websiteForEmployment &&
      !values.websiteForEmployment.match(/^https?:\/\/[a-z0-9-_.]{2,}\.[a-z0-9]{2,}/i)
    ) {
      errors.websiteForEmployment = 'Please provide a valid URL';
    }

    return errors;
  };

  const submit = (values: {websiteForEmployment?: string}) => () => onSubmit(values);

  return (
    <div className="account-settings-form">
      <Formik
        initialValues={{websiteForEmployment}}
        validate={validateWebsiteForEmployment}
        onSubmit={(values) => onSubmit(values)}
      >
        {({values, errors}) => (
          <>
            <div className="account-settings-form-title borderless">
              <Text header emphasis weight={2}>
                Trust and Verification
              </Text>
            </div>
            <div className="registration-section-basic-info-title">
              <Text showOverflow>
                We want to make sure that students&apos; privacy is protected. The best way to do
                this is to verify your identify before enabling you to see any student information.
                You only have to do this once and all your information will be secure.
              </Text>
            </div>
            <Text header emphasis weight={3}>
              Official ID
            </Text>
            <Text showOverflow>
              Upload a photo of your school or government issued ID. You can easily take a picture
              with your computer or mobile camera.
            </Text>
            <Text>
              <Icon iconType="lock-alt" /> No one will ever see your ID.
            </Text>
            <div className="text-right">
              <OfficialIdUploader editing={false} />
            </div>
            <Form>
              <Label>
                <Text header emphasis weight={3}>
                  Link to website with proof that you work at a school / non-profit.
                </Text>
                <Field name="websiteForEmployment" type="text">
                  {({field: {name, value}, form: {setFieldValue}}) => (
                    <TextInput
                      placeholder="https://www.example.com"
                      value={value}
                      onChange={updateField(setFieldValue, name)}
                      error={errors[name]}
                    />
                  )}
                </Field>
              </Label>
            </Form>
            <Text header emphasis weight={3}>
              Connect to your Social Networks
            </Text>
            <Text showOverflow>
              Connecting to your social profiles helps to verify your identity and build trust.
            </Text>
            {linkedinConnected ? (
              <Button color="negative" onClick={disconnectLinkedin}>
                Disconnect LinkedIn
              </Button>
            ) : (
              <Button onClick={goToLinkedinAuth}>Connect to LinkedIn</Button>
            )}
            <AccountSettingsFormFooter handleSubmit={submit(values)} submitting={false} />
          </>
        )}
      </Formik>
    </div>
  );
};

export default TrustAndVerification;
